import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactTable from "../General/ReactTable";
import SideMenu from "../General/SideMenu";
import Messagedialogbox, {
  Pre_Loading,
  Show_Message,
} from "../General/Messagedialogbox";
import {
  API_FETCH,
  API_POST,
  API_POSTParem,
  DecryptData,
} from "../General/Utility";
import { useDispatch, useSelector } from "react-redux";
import CustomizedSelectoption from "../General/CustomizedSelectoption";

// ---------- constants / helpers ----------
const DEFAULT_SCR_ID = 71; // Inward Challan (fallback = first item if this id is not in the company DB)
const DEFAULT_REASON_ID = 4; // Production (fallback = first item if this id is not in the company DB)
const TAB_PENDING = "Pending";
const TAB_COMPLETED = "Completed";
const REMARK_MAX = 200; // keep same as DB column length
const EMPTY_PARAMS = {};

// Confirmation popup. Only this ONE function depends on the dialog type.
// (window.confirm for now - swap with the POS confirm dialog later)
const confirmBox = (strQuestion) => window.confirm(strQuestion);

// avoids floating point issues like 0.1 + 0.2
const round3 = (n) => Math.round((Number(n) + Number.EPSILON) * 1000) / 1000;

// 2026-09-24 / 2026-09-24T00:00:00  ->  24-09-2026
const formatDate = (v) => {
  if (!v) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(v);
};

// business rule: "Production" reason must be linked with a Production Voucher
// (change only this one function if the rule is different)
const isProductionReason = (name) => /production/i.test(name || "");

const getDefaultReasonId = (reasons) => {
  const list = reasons || [];
  if (list.some((r) => Number(r.iAdjustTypeId) === DEFAULT_REASON_ID))
    return DEFAULT_REASON_ID;
  return list.length ? Number(list[0].iAdjustTypeId) : 0;
};

// server model binder rejects null strings, so null -> "" before posting
const nullToEmpty = (obj) =>
  JSON.parse(JSON.stringify(obj, (k, v) => (v === null ? "" : v)));

// ---- Branch + DB name (needed by every API of this screen) ----
// ONE place only. If the session key names in your project are different,
// change the key lists below. Available keys are printed in the browser
// console (F12) when something is not found.
const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "")
      return obj[k];
  }
  return undefined;
};
const readSession = async () => {
  let p = {};
  try {
    p = (await DecryptData(localStorage.jbctrlparams)) || {};
  } catch (e) {
    p = {};
  }
  const strDBName = pick(p, ["DBName", "DB", "db", "strDBName", "CompanyDB"]) ?? localStorage.db;
  const iBranchId = Number(
    pick(p, ["BranchId", "Branch", "iBranchId", "BranchID"]) ?? localStorage.BranchId
  );
  if (!strDBName || Number.isNaN(iBranchId)) {
    console.error("Session keys available:", Object.keys(p));
    return null;
  }
  return { strDBName, iBranchId };
};

function TransactionAdjustment() {
  const dispatch = useDispatch();
  const _selecrparem = useSelector(
    (state) => (state.SelectOption && state.SelectOption.parems) || EMPTY_PARAMS
  );

  const [objVouchType, setobjVouchType] = useState([]);
  const [objPOVoucher, setobjPOVoucher] = useState([]);
  const [objReason, setobjReason] = useState([]);
  const [arrEntVouchers, setarrEntVouchers] = useState([]);
  const [selected, setselected] = useState(-1); // -1 = nothing selected
  const [strSearch, setstrSearch] = useState("");
  const [iScrId, setiScrId] = useState(DEFAULT_SCR_ID);
  const [iAdjustTypeId, setiAdjustTypeId] = useState(DEFAULT_REASON_ID);
  const [iPOId, setiPOId] = useState(0);
  const [dQty, setdQty] = useState(""); // string on purpose (so "0." / "1.5" can be typed)
  const [strProName, setstrProName] = useState("");
  const [strRemark, setstrRemark] = useState("");
  const [strTabName, setstrTabName] = useState(TAB_PENDING);

  const ispending = strTabName === TAB_PENDING;

  const busyRef = useRef(false); // blocks double submit (Save / Delete)
  const reqIdRef = useRef(0); // ignores stale grid responses
  const tabRefs = useRef([]);
  const tabLineRef = useRef(null);

  useEffect(() => {
    Load_TransactionAdjustment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // tab underline position (no jQuery / no DOM hacks)
  useLayoutEffect(() => {
    const place = () => {
      const lbl = tabRefs.current[strTabName === TAB_PENDING ? 0 : 1];
      const line = tabLineRef.current;
      if (lbl && line) {
        line.style.left = lbl.offsetLeft + "px";
        line.style.width = lbl.clientWidth + "px";
      }
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [strTabName]);

  const AlertMessage = (Message, Icon) => {
    Show_Message(dispatch, Message, Icon);
  };

  // returns { strDBName, iBranchId } or null (message already shown)
  const getSess = async () => {
    const sess = await readSession();
    if (!sess) {
      AlertMessage(
        "Branch / DB information not found in session. Please check readSession() in TransactionAdjustment.js",
        "error"
      );
    }
    return sess;
  };

  // ---------- data loading ----------
  const resetEntry = () => {
    setselected(-1);
    setstrProName("");
    setdQty("");
    setstrRemark("");
  };

  // gets grid data. no loader here (caller controls it).
  // API_POST already shows the error message and returns undefined on failure.
  const fetchGrid = async (tabName, scrId, search) => {
    const sess = await getSess();
    if (!sess) return;
    const myReq = ++reqIdRef.current;
    const Res = await API_POST(
      "TransactionAdjustment/LoadVouchers",
      {
        strColName: tabName,
        iScrId: scrId,
        strSearch: (search || "").trim(),
        iBranchId: sess.iBranchId,
        strDBName: sess.strDBName,
      },
      dispatch
    );
    if (myReq !== reqIdRef.current) return; // newer request already fired
    if (Res && Array.isArray(Res.objVoucherList)) {
      setarrEntVouchers(Res.objVoucherList);
      resetEntry();
    }
  };

  const loadGrid = async (
    tabName = strTabName,
    scrId = iScrId,
    search = strSearch
  ) => {
    await Pre_Loading(dispatch, true);
    try {
      await fetchGrid(tabName, scrId, search);
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      await Pre_Loading(dispatch, false);
    }
  };

  const Load_TransactionAdjustment = async () => {
    await Pre_Loading(dispatch, true);
    try {
      const sess = await getSess();
      if (!sess) return;
      const Res = await API_FETCH(
        `TransactionAdjustment/Load_TransAdjustment/${DEFAULT_SCR_ID}/${
          sess.iBranchId
        }/${encodeURIComponent(sess.strDBName)}`,
        dispatch
      );
      if (!Res) return;

      const types = Res.objVouchType || [];
      const reasons = Res.objReason || [];
      setobjVouchType(types);
      setobjReason(reasons);
      setobjPOVoucher(Res.objPOVoucher || []);
      setiAdjustTypeId(getDefaultReasonId(reasons));

      const scr = types.some((t) => Number(t.iScrId) === DEFAULT_SCR_ID)
        ? DEFAULT_SCR_ID
        : types.length
        ? Number(types[0].iScrId)
        : DEFAULT_SCR_ID;
      setiScrId(scr);

      if (scr === DEFAULT_SCR_ID && Array.isArray(Res.objPendingData)) {
        setarrEntVouchers(Res.objPendingData);
      } else {
        await fetchGrid(TAB_PENDING, scr, "");
      }
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      await Pre_Loading(dispatch, false);
    }
  };

  // ---------- unsaved-data guard ----------
  const HasUnsaved = () =>
    ispending && arrEntVouchers.some((r) => Number(r.qty) > 0);

  const confirmIfUnsaved = (action) => {
    if (
      HasUnsaved() &&
      !confirmBox("Unsaved adjustments will be lost. Do you want to continue?")
    )
      return;
    action();
  };

  // ---------- tab / type / search ----------
  const tab_click = (tabName) => {
    if (tabName === strTabName) return;
    confirmIfUnsaved(() => {
      setstrTabName(tabName);
      setarrEntVouchers([]); // avoid old columns/data flash
      loadGrid(tabName, iScrId, strSearch);
    });
  };

  const VoucherTypeChange = (val) => {
    const id = Number(val);
    if (id === iScrId) return;
    confirmIfUnsaved(() => {
      setiScrId(id);
      setarrEntVouchers([]);
      loadGrid(strTabName, id, strSearch);
    });
  };

  const SearchAdjustMent = () => {
    confirmIfUnsaved(() => loadGrid(strTabName, iScrId, strSearch));
  };

  // ---------- row select / add ----------
  const SelectRow = (rowInfo, Index) => {
    if (!ispending || !rowInfo) return;
    const idx =
      Number.isInteger(Index) &&
      arrEntVouchers[Index] &&
      arrEntVouchers[Index].sNo === rowInfo.sNo
        ? Index
        : arrEntVouchers.findIndex((r) => r.sNo === rowInfo.sNo);
    if (idx < 0) return;
    const row = arrEntVouchers[idx];
    setselected(idx);
    setstrProName(row.proName || "");
    setdQty(Number(row.qty) > 0 ? String(row.qty) : "");
    setstrRemark(row.remark || "");
    setTimeout(() => {
      const el = document.getElementById("txtproQty");
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
  };

  const AddClick = () => {
    if (!ispending) return;
    if (selected < 0 || !arrEntVouchers[selected]) {
      AlertMessage("Please select a row from the grid first", "info");
      return;
    }
    const row = arrEntVouchers[selected];
    const qty = round3(dQty === "" ? 0 : dQty);
    const pending = round3(row.pendingQty);

    if (Number.isNaN(qty) || qty < 0) {
      AlertMessage("Please enter a valid Qty", "info");
      return;
    }
    if (qty === 0 && !(Number(row.qty) > 0)) {
      AlertMessage("Qty should be greater than 0", "info");
      return;
    }
    if (qty > pending) {
      AlertMessage("Only " + pending + " Qty is available to adjust.", "info");
      return;
    }

    const reasonObj = objReason.find(
      (r) => Number(r.iAdjustTypeId) === iAdjustTypeId
    );
    const isProd = isProductionReason(reasonObj && reasonObj.strAdjustType);

    if (qty > 0) {
      if (!iAdjustTypeId || !reasonObj) {
        AlertMessage("Please select a Reason", "info");
        return;
      }
      if (isProd && !iPOId) {
        AlertMessage("Please select a Production Voucher", "info");
        return;
      }
    }
    const poObj = isProd
      ? objPOVoucher.find((p) => Number(p.iPOId) === iPOId)
      : null;

    // reason / production voucher are stamped PER ROW at Add time
    const updated = arrEntVouchers.map((r, i) => {
      if (i !== selected) return r;
      if (qty === 0) {
        return {
          ...r,
          qty: 0,
          remark: "",
          adjustTypeId: 0,
          adjustType: "",
          poId: 0,
          poCode: "",
        };
      }
      return {
        ...r,
        qty: qty,
        remark: strRemark.trim(),
        adjustTypeId: iAdjustTypeId,
        adjustType: reasonObj.strAdjustType || "",
        poId: isProd ? iPOId : 0,
        poCode: isProd && poObj ? poObj.strPOName || "" : "",
      };
    });
    setarrEntVouchers(updated);
    resetEntry();
  };

  const QtyKeyDown = (e) => {
    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
    if (e.key === "Enter") AddClick();
  };

  const handleClick = (event) => {
    event.target.select();
  };

  // ---------- save / delete / clear / close ----------
  const runBusy = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    await Pre_Loading(dispatch, true);
    try {
      await fn();
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      busyRef.current = false;
      await Pre_Loading(dispatch, false);
    }
  };

  // step 1 : validations, then confirmation
  const SaveClick = () => {
    if (!ispending) {
      AlertMessage("Save is available only in Pending tab", "info");
      return;
    }
    const rows = arrEntVouchers.filter((r) => Number(r.qty) > 0);
    if (rows.length === 0) {
      AlertMessage("Please add Qty for at least one row before saving", "info");
      return;
    }
    if (
      selected >= 0 &&
      arrEntVouchers[selected] &&
      Number(dQty) > 0 &&
      round3(dQty) !== round3(arrEntVouchers[selected].qty)
    ) {
      AlertMessage(
        "Qty entered for '" + strProName + "' is not added. Click Add first.",
        "info"
      );
      return;
    }
    for (const r of rows) {
      if (round3(r.qty) > round3(r.pendingQty)) {
        AlertMessage(
          "Qty is more than Pending Qty for voucher " + r.vouchNo,
          "info"
        );
        return;
      }
      if (!r.adjustTypeId) {
        AlertMessage("Reason is missing for voucher " + r.vouchNo, "info");
        return;
      }
    }
    if (!confirmBox("Do You Want to Update Adjustment ?")) return;
    UpdateClick(rows);
  };

  // step 2 : actual save
  const UpdateClick = (rows) =>
    runBusy(async () => {
      const sess = await getSess();
      if (!sess) return;
      const Res = await API_POST(
        "TransactionAdjustment/InsUpd_TransAdjustment",
        {
          objEntTAP: nullToEmpty(rows),
          iBranchId: sess.iBranchId,
          strDBName: sess.strDBName,
        },
        dispatch
      );
      if (!Res) return; // error / validation message already shown
      setiPOId(0);
      setiAdjustTypeId(getDefaultReasonId(objReason));
      AlertMessage(Res.strMessage, "success");
      await fetchGrid(strTabName, iScrId, strSearch);
    });

  const DeleteClick = (iTransAdjId) => {
    if (!iTransAdjId) {
      AlertMessage("Invalid adjustment", "error");
      return;
    }
    if (!confirmBox("Do You Want to Delete Adjustment?")) return;
    runBusy(async () => {
      const sess = await getSess();
      if (!sess) return;
      const Res = await API_POSTParem(
        "TransactionAdjustment/Delete_TransAdjustment/" +
          iTransAdjId +
          "/" +
          encodeURIComponent(sess.strDBName),
        dispatch
      );
      if (!Res) return;
      AlertMessage(Res.strMessage, "success");
      await fetchGrid(strTabName, iScrId, strSearch);
    });
  };

  const doClear = () => {
    setstrSearch("");
    setiPOId(0);
    setiAdjustTypeId(getDefaultReasonId(objReason));
    resetEntry();
    loadGrid(strTabName, iScrId, "");
  };
  const ClearClick = () => confirmIfUnsaved(doClear);

  const CloseClick = () =>
    confirmIfUnsaved(() => {
      window.location.href = "/Dashboard";
    });

  // ---------- grid columns ----------
  const columnsPendingVoucher = [
    {
      Header: "SNo",
      accessor: "sNo",
      minWidth: 50,
      maxWidth: 50,
      style: { justifyContent: "center" },
    },
    {
      Header: "Vendor Name",
      accessor: "venName",
      style: { justifyContent: "left" },
      minWidth: 200,
    },
    {
      Header: "Vouch No",
      accessor: "vouchNo",
      style: { justifyContent: "left" },
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Date",
      accessor: "vouchDate",
      style: { justifyContent: "center" },
      minWidth: 100,
      maxWidth: 100,
      cell: ({ rowinfo }) => formatDate(rowinfo.vouchDate),
    },
    {
      Header: "Product Name",
      accessor: "proName",
      minWidth: 250,
      style: { justifyContent: "left" },
    },
    {
      Header: "Actual Qty",
      accessor: "actualQty",
      style: { justifyContent: "right" },
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Convert Qty",
      accessor: "convertedQty",
      style: { justifyContent: "right" },
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Pending Qty",
      accessor: "pendingQty",
      style: { justifyContent: "right" },
      minWidth: 100,
      maxWidth: 100,
    },
    {
      Header: "Unit",
      accessor: "unit",
      style: { justifyContent: "center" },
      minWidth: 80,
      maxWidth: 80,
    },
    {
      Header: "Qty",
      accessor: "qty",
      style: { justifyContent: "right" },
      minWidth: 90,
      maxWidth: 90,
    },
    {
      Header: "Reason",
      accessor: "adjustType",
      style: { justifyContent: "center" },
      minWidth: 110,
      maxWidth: 110,
    },
    {
      Header: "Prod. Voucher",
      accessor: "poCode",
      style: { justifyContent: "center" },
      minWidth: 110,
      maxWidth: 110,
    },
    {
      Header: "Remark",
      accessor: "remark",
      style: { justifyContent: "left" },
      minWidth: 150,
      maxWidth: 150,
    },
  ];

  const columnsCompVoucher = [
    {
      Header: "SNo",
      accessor: "sNo",
      minWidth: 50,
      maxWidth: 50,
      style: { justifyContent: "center" },
    },
    {
      Header: "Vendor Name",
      accessor: "venName",
      style: { justifyContent: "left" },
      minWidth: 200,
    },
    {
      Header: "Vouch No",
      accessor: "vouchNo",
      minWidth: 100,
      maxWidth: 100,
      style: { justifyContent: "left" },
    },
    {
      Header: "Date",
      accessor: "vouchDate",
      minWidth: 100,
      maxWidth: 100,
      style: { justifyContent: "center" },
      cell: ({ rowinfo }) => formatDate(rowinfo.vouchDate),
    },
    {
      Header: "Product Name",
      accessor: "proName",
      minWidth: 200,
      style: { justifyContent: "left" },
    },
    {
      Header: "Convert Qty",
      accessor: "convertedQty",
      minWidth: 100,
      maxWidth: 100,
      style: { justifyContent: "right" },
    },
    {
      Header: "Unit",
      accessor: "unit",
      minWidth: 80,
      maxWidth: 80,
      style: { justifyContent: "center" },
    },
    {
      Header: "AdjustType",
      accessor: "adjustType",
      minWidth: 110,
      maxWidth: 110,
      style: { justifyContent: "center" },
    },
    {
      Header: "Production Voucher",
      accessor: "poCode",
      minWidth: 130,
      maxWidth: 130,
      style: { justifyContent: "center" },
    },
    {
      Header: "Remark",
      accessor: "remark",
      minWidth: 150,
      maxWidth: 150,
      style: { justifyContent: "left" },
    },
    {
      Header: "Options",
      minWidth: 60,
      maxWidth: 60,
      style: { justifyContent: "center" },
      cell: ({ rowinfo }) => (
        <i
          role="button"
          title="Delete"
          style={{ cursor: "pointer" }}
          onClick={() => DeleteClick(rowinfo.traAdjId)}
          className="far fa-trash-alt"
        ></i>
      ),
    },
  ];

  return (
    <>
      <SideMenu ScrId={77} />
      <div className="Master-container">
        <div className="Master-card1">
          <div className="Master-wrapper">
            <div className="fg-card shadow-base mt-2">
              <div className="trans-tab">
                <div className="trans-buttons">
                  <label
                    ref={(el) => (tabRefs.current[0] = el)}
                    onClick={() => tab_click(TAB_PENDING)}
                    className={ispending ? "active" : ""}
                  >
                    Pending
                  </label>
                  <label
                    ref={(el) => (tabRefs.current[1] = el)}
                    onClick={() => tab_click(TAB_COMPLETED)}
                    className={!ispending ? "active" : ""}
                  >
                    Completed
                  </label>
                  <div id="tab-line" ref={tabLineRef}></div>
                </div>
              </div>

              {/* row 1 : voucher type (left) + search (right) */}
              <div className="row p-2 d-flex justify-content-between">
                <div className="col-md-3 col-sm-12 mt-1">
                  <CustomizedSelectoption
                    optionparem="isoptionShow"
                    showoption={_selecrparem.isoptionShow}
                    placeholder="Select Voucher Type"
                    select_value={VoucherTypeChange}
                    btnname=""
                    btnshow={false}
                    show_popup={""}
                    valueonly={true}
                    defaultval={iScrId}
                    displayName="strScrName"
                    disvalue="iScrId"
                    arrydata={objVouchType}
                    EmptVal="Voucher Type"
                  />
                </div>
                <div className="col-md-4 col-sm-12 mt-1">
                  <div className="m-hsearch">
                    <input
                      type="text"
                      autoCorrect="off"
                      autoComplete="off"
                      placeholder="Search Transaction Adjustment"
                      value={strSearch}
                      onChange={(e) => setstrSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") SearchAdjustMent();
                      }}
                      autoFocus
                    />
                    <i
                      className="fas fa-search"
                      role="button"
                      title="Search"
                      style={{ cursor: "pointer" }}
                      onClick={SearchAdjustMent}
                    ></i>
                  </div>
                </div>
              </div>

              {/* row 2 + 3 : entry section (Pending tab only) */}
              {ispending && (
                <div className="row p-2">
                  <div className="col-sm-12 col-md-4 mt-1">
                    <label>Product Name</label>
                    <input
                      type="text"
                      aria-label="Product Name"
                      className="form-control"
                      name="strProName"
                      value={strProName}
                      placeholder="Select a row from the grid"
                      readOnly
                    />
                  </div>
                  <div className="col-sm-12 col-md-2 mt-1">
                    <label>Qty</label>
                    <input
                      type="number"
                      aria-label="Qty"
                      className="form-control"
                      autoComplete="off"
                      name="dQty"
                      id="txtproQty"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      placeholder="0"
                      value={dQty}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v !== "" && Number(v) < 0) return;
                        setdQty(v);
                      }}
                      onKeyDown={QtyKeyDown}
                      onClick={handleClick}
                    />
                  </div>
                  <div className="col-sm-12 col-md-3 mt-1">
                    <label>Reason</label>
                    <CustomizedSelectoption
                      optionparem="isoptionShow1"
                      showoption={_selecrparem.isoptionShow1}
                      placeholder="Select Reason"
                      select_value={(val) => setiAdjustTypeId(Number(val))}
                      btnname=""
                      btnshow={false}
                      show_popup={""}
                      valueonly={true}
                      defaultval={iAdjustTypeId}
                      displayName="strAdjustType"
                      disvalue="iAdjustTypeId"
                      arrydata={objReason}
                      EmptVal="Reason"
                    />
                  </div>
                  <div className="col-sm-12 col-md-3 mt-1">
                    <label>Production Voucher</label>
                    <CustomizedSelectoption
                      optionparem="isoptionShow2"
                      showoption={_selecrparem.isoptionShow2}
                      placeholder="Select Production Voucher"
                      select_value={(val) => setiPOId(Number(val))}
                      btnname=""
                      btnshow={false}
                      show_popup={""}
                      valueonly={true}
                      defaultval={iPOId}
                      displayName="strPOName"
                      disvalue="iPOId"
                      arrydata={objPOVoucher}
                      EmptVal="Production Voucher"
                    />
                  </div>
                  <div className="col-sm-12 col-md-3 mt-1">
                    <label>Remark</label>
                    <input
                      type="text"
                      className="form-control"
                      name="txtRemark"
                      autoComplete="off"
                      id="txtRemark"
                      maxLength={REMARK_MAX}
                      value={strRemark}
                      onChange={(e) => setstrRemark(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") AddClick();
                      }}
                      onClick={handleClick}
                      placeholder="Remark"
                    />
                  </div>
                  <div className="col-sm-12 col-md-2 mt-4">
                    <div className="btn-sub-section">
                      <button className="btn-fabgreen" onClick={AddClick}>
                        Add&nbsp;<i className="bx bx-layer-plus"></i>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {ispending && (
              <ReactTable
                columns={columnsPendingVoucher}
                data={arrEntVouchers}
                minRows={10}
                row_click={(rowInfo, Index) => SelectRow(rowInfo, Index)}
                row_doubleclick={(rowInfo, Index) => SelectRow(rowInfo, Index)}
                background={true}
                className="full-table"
                containerclass="autotable"
              />
            )}
            {!ispending && (
              <ReactTable
                columns={columnsCompVoucher}
                data={arrEntVouchers}
                minRows={10}
                row_click={() => {}}
                row_doubleclick={() => {}}
                background={true}
                className="full-table"
                containerclass="autotable"
              />
            )}
            <Messagedialogbox />
          </div>
          <div className="btn-section">
            <button
              className="btn-fabgreen"
              onClick={SaveClick}
              disabled={!ispending}
            >
              Save&nbsp;<i className="bx bxs-save"></i>
            </button>
            <button className="btn-fabgreen" onClick={ClearClick}>
              Clear&nbsp;<i className="fa-solid fa-broom"></i>
            </button>
            <button className="btn-fabgreen" onClick={CloseClick}>
              Close&nbsp;<i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default TransactionAdjustment;